/* v3.18-1289 — EMAIL-ARCH-002 Slice 2
 *
 * Universal Compose & Approve dialog. One component. Every manually-
 * initiated email in the Hub eventually routes through this component;
 * template-specific compose dialogs are banned per operator directive.
 *
 * Contract:
 *   • Templates are loaded from GET /email/compose/templates.
 *   • Envelope is prepared by POST /email/compose/prepare — the backend
 *     resolves the dynamic sender signature (never hardcoded).
 *   • Draft persistence via /email/compose/drafts.
 *   • Attachments carried inline as base64 (per-file 10 MB cap /
 *     total 25 MB cap enforced server-side too).
 *   • Send via POST /email/compose/send — backend delivers through the
 *     Hub's unified outbound gateway (SMTP2GO) with a full audit row.
 *   • Reply / forward: pass ``initial`` prop with in_reply_to /
 *     thread_root_id and pre-filled subject/body.
 *
 * Usage:
 *   <ComposeAndApprove
 *      open={openComposer}
 *      onOpenChange={setOpenComposer}
 *      initial={{ to:['jane@example.com'], client_id, context:{ client_first_name:'Jane' } }}
 *      onSent={({ audit_id, delivered }) => ...}
 *   />
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import ClientLocalTime from './ClientLocalTime';
import {
  Bold, Italic, Underline, List as ListIcon,
  Paperclip, Save, Send, Loader2, Trash2, X, Reply, Forward, FileText, BookOpen,
  ChevronDown, ChevronUp, RefreshCw,
} from 'lucide-react';
import EmailTemplatePickerDialog from './EmailTemplatePickerDialog';

const MAX_ATTACHMENT_BYTES       = 10 * 1024 * 1024;   // 10 MB
const MAX_TOTAL_ATTACHMENT_BYTES = 25 * 1024 * 1024;   // 25 MB

const emptyEnvelope = {
  envelope_id:     '',
  template_key:    'blank',
  subject:         '',
  from_name:       '',
  from_address:    '',
  reply_to:        '',
  to:              [],
  cc:              [],
  bcc:             [],
  body:            '',
  signature_lines: [],
  signature_text:  '',
  signature_html:  '',
  brand_tagline:   'Powered by Prestigious Companies',
  in_reply_to:     null,
  thread_root_id:  null,
};

function chipsFromString(s) {
  return (s || '')
    .split(/[,;\s]+/g)
    .map((x) => x.trim())
    .filter(Boolean);
}

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // reader.result is a data URL — strip the prefix
      const s = String(reader.result || '');
      const comma = s.indexOf(',');
      resolve(comma >= 0 ? s.slice(comma + 1) : s);
    };
    reader.onerror = () => reject(reader.error || new Error('read failure'));
    reader.readAsDataURL(file);
  });
}

/* Very small rich-text editor built on a contentEditable div — the
 * body is captured as plain text (paragraphs preserved via
 * \n\n separators). Toolbar drives document.execCommand for the
 * shape-of-life needs (bold / italic / underline / bullet list),
 * which is sufficient for operator emails without pulling in
 * a heavy editor dependency. */
function RichTextArea({ value, onChange, disabled, testId }) {
  const ref = useRef(null);

  // Keep DOM in sync when value changes externally (draft load, template load, reply)
  useEffect(() => {
    if (ref.current && ref.current.innerText !== value) {
      ref.current.innerText = value || '';
    }
  }, [value]);

  const cmd = (name) => {
    if (disabled) return;
    document.execCommand(name);
    if (ref.current) onChange(ref.current.innerText);
  };

  return (
    <div className="border rounded-md bg-white">
      <div className="flex items-center gap-1 border-b px-2 py-1 bg-slate-50">
        <button type="button" title="Bold"      onClick={() => cmd('bold')}      className="p-1 rounded hover:bg-slate-200" data-testid="compose-btn-bold">      <Bold      size={14} /></button>
        <button type="button" title="Italic"    onClick={() => cmd('italic')}    className="p-1 rounded hover:bg-slate-200" data-testid="compose-btn-italic">    <Italic    size={14} /></button>
        <button type="button" title="Underline" onClick={() => cmd('underline')} className="p-1 rounded hover:bg-slate-200" data-testid="compose-btn-underline"> <Underline size={14} /></button>
        <button type="button" title="Bullet list" onClick={() => cmd('insertUnorderedList')} className="p-1 rounded hover:bg-slate-200" data-testid="compose-btn-bullet"><ListIcon size={14} /></button>
      </div>
      <div
        ref={ref}
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={(e) => onChange(e.currentTarget.innerText)}
        className="min-h-[220px] max-h-[420px] overflow-auto px-3 py-2 text-sm outline-none whitespace-pre-wrap"
        data-testid={testId || 'compose-body-editor'}
      />
    </div>
  );
}

/* Attachment row */
function AttachmentRow({ att, onRemove }) {
  return (
    <div className="flex items-center justify-between text-xs bg-slate-100 rounded px-2 py-1 gap-2" data-testid={`compose-att-${att.filename}`}>
      <div className="flex items-center gap-1 truncate">
        <FileText size={12} />
        <span className="truncate">{att.filename}</span>
        <span className="text-slate-500">({Math.ceil(att._size / 1024)} KB)</span>
      </div>
      <button type="button" onClick={onRemove} className="text-red-600 hover:text-red-800" data-testid={`compose-att-remove-${att.filename}`}><X size={12} /></button>
    </div>
  );
}

/* v3.21-1621 · Select-Client recipient picker.
 *
 * Uses the authoritative `/api/clients?search=` endpoint so any
 * authenticated Hub user can pick a client.  Client 1 (primary email)
 * and Client 2 (secondary_email) are shown as SEPARATE selectable
 * rows when both exist, so a rep never accidentally emails the wrong
 * household member.  Manual free-text email entry in the To field
 * remains fully functional even after opening this picker.
 */
function ClientRecipientPicker({ open, setOpen, current, onPick }) {
  const [q, setQ] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const term = q.trim();
    if (term.length < 2) { setRows([]); return undefined; }
    const h = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get('/clients', { params: { search: term, limit: 15 } });
        // API returns either a plain array or {clients:[...]} depending on the route branch.
        const list = Array.isArray(r.data) ? r.data : (r.data?.clients || []);
        setRows(list);
      } catch { setRows([]); }
      finally { setLoading(false); }
    }, 300);
    return () => clearTimeout(h);
  }, [q, open]);

  useEffect(() => {
    if (!open) return undefined;
    const h = (e) => {
      if (!boxRef.current) return;
      if (!boxRef.current.contains(e.target)) setOpen(false);
    };
    window.addEventListener('mousedown', h);
    return () => window.removeEventListener('mousedown', h);
  }, [open, setOpen]);

  // Explode each row into up to two recipient options (Client 1 + Client 2).
  const options = useMemo(() => {
    const out = [];
    for (const c of rows) {
      const primaryEmail = (c.email || '').trim();
      const secondary   = (c.secondary_email || '').trim();
      const secondaryName = c.secondary_owner_name || c.client_2_name || 'Client 2';
      if (primaryEmail) {
        out.push({
          id:            c.id,
          name:          c.full_name || 'Client',
          email:         primaryEmail,
          first_name:    (c.full_name || '').split(' ')[0] || '',
          is_secondary:  false,
          _key:          `${c.id}::1`,
        });
      }
      if (secondary) {
        out.push({
          id:            c.id,
          name:          secondaryName,
          email:         secondary,
          first_name:    (secondaryName || '').split(' ')[0] || '',
          is_secondary:  true,
          _key:          `${c.id}::2`,
        });
      }
    }
    return out;
  }, [rows]);

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-[11px] text-slate-700 border border-slate-300 rounded px-2 py-0.5 hover:bg-slate-100"
        data-testid="compose-select-client-btn"
        title="Pick a recipient from the Hub client directory (Client 2 supported)"
      >
        {current ? 'Change client…' : 'Select client…'}
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-80 rounded-md border border-slate-200 bg-white shadow-lg z-30 p-2"
             data-testid="compose-client-picker">
          <input
            autoFocus
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name / email / phone…"
            className="w-full text-sm rounded border border-slate-300 px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-slate-400"
            data-testid="compose-client-picker-search"
          />
          <div className="mt-1 max-h-64 overflow-y-auto">
            {loading && (
              <div className="text-xs text-slate-500 flex items-center gap-1 py-1"><Loader2 className="h-3 w-3 animate-spin" /> searching…</div>
            )}
            {!loading && q.trim().length < 2 && (
              <div className="text-[11px] text-slate-500 py-1">Type at least 2 characters</div>
            )}
            {!loading && q.trim().length >= 2 && options.length === 0 && (
              <div className="text-xs text-slate-500 py-1" data-testid="compose-client-picker-empty">No clients matched</div>
            )}
            {options.map((o) => (
              <button
                key={o._key}
                type="button"
                onClick={() => onPick(o)}
                className="w-full text-left rounded px-2 py-1 hover:bg-emerald-50 border-b border-slate-50 last:border-0"
                data-testid={`compose-client-picker-option-${o._key}`}
              >
                <div className="text-sm font-medium truncate flex items-center gap-1">
                  <span className="truncate">{o.name}</span>
                  {o.is_secondary && <span className="text-[9px] font-bold text-emerald-700 bg-emerald-100 border border-emerald-300 px-1 rounded">Client 2</span>}
                </div>
                <div className="text-[10px] text-slate-500 truncate">{o.email}</div>
              </button>
            ))}
          </div>
          <div className="text-[10px] text-slate-400 pt-1 border-t border-slate-100 mt-1">
            Or just keep typing an email address in the To field.
          </div>
        </div>
      )}
    </div>
  );
}


export default function ComposeAndApprove({
  open,
  onOpenChange,
  initial = {},
  onSent,
  onCustomSend,
}) {
  // v3.21-1845 · Only the generic /email/compose/send gateway embeds the signature.
  const signatureAppended = typeof onCustomSend !== 'function';
  const [templates, setTemplates]     = useState([]);
  const [templateKey, setTemplateKey] = useState(initial.template_key || 'blank');
  const [context, setContext]         = useState(initial.context || {});
  const [envelope, setEnvelope]       = useState(emptyEnvelope);

  const [toStr, setToStr]     = useState((initial.to || []).join(', '));
  const [ccStr, setCcStr]     = useState((initial.cc || []).join(', '));
  const [bccStr, setBccStr]   = useState((initial.bcc || []).join(', '));
  const [subject, setSubject] = useState(initial.subject || '');
  const [body, setBody]       = useState(initial.body || '');

  const [attachments, setAttachments] = useState([]);   // {filename,mime_type,data_base64,_size}
  const [drafts, setDrafts]           = useState([]);
  const [draftId, setDraftId]         = useState(initial.draft_id || null);

  const [preparing, setPreparing]     = useState(false);
  const [sending, setSending]         = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);   // v3.18-1302 EMAIL-ARCH-003
  const [libraryTpl,  setLibraryTpl]  = useState(null);

  // v3.21-1621 · Select-Client recipient picker.  When present, this
  // overrides `initial.client_id` for the outgoing send payload so
  // reps can compose an email to any client from the Hub (including
  // Client 2 on a household deal) without leaving the composer, while
  // manual free-text email entry in the To field still works.
  const [clientOverride, setClientOverride] = useState(null); // {id, name, email, first_name, is_secondary?}
  const [clientPickerOpen, setClientPickerOpen] = useState(false);

  // v3.18-1304 — Live Signature Preview state.  Uses the SAME
  // ``services.email_signature.build_signature_html`` pipeline the
  // ``/email/compose/send`` endpoint uses, so what the operator sees
  // here matches the delivered email byte-for-byte.
  const [sigPreview, setSigPreview] = useState({
    signature_html: '',
    signature_lines: [],
    signature_text: '',
    profile_used: null,
    version: '',
    renderer: '',
  });
  const [sigOpen, setSigOpen] = useState(true);            // default expanded per spec
  const [sigLoading, setSigLoading] = useState(false);
  const [sigError, setSigError] = useState(null);
  const fileInputRef = useRef(null);

  const clientId       = clientOverride?.id || initial.client_id || null;
  const inReplyTo      = initial.in_reply_to     || null;
  const threadRootId   = initial.thread_root_id  || inReplyTo || null;

  const totalAttBytes = useMemo(
    () => attachments.reduce((n, a) => n + (a._size || 0), 0),
    [attachments],
  );

  /* v3.18-1303 — Operator-work autosave.  Every keystroke persists
   * to localStorage under a per-client (or per-envelope) key so the
   * message survives:
   *   • a full-screen crash (error boundary recovery flow)
   *   • an accidental modal close
   *   • a page refresh
   *   • a Twilio SDK unmount
   * The value is trimmed to compose state only — attachments are
   * NOT autosaved (base64 payloads can push localStorage past its
   * 5 MB cap).  Restore only fires when the modal is opened without
   * an existing draft_id.
   */
  const autosaveKey = useMemo(() => {
    return `compose:autosave:${clientId || 'global'}:${inReplyTo || 'root'}`;
  }, [clientId, inReplyTo]);

  const restoredRef = useRef(false);
  useEffect(() => {
    if (!open) { restoredRef.current = false; return; }
    if (restoredRef.current) return;
    restoredRef.current = true;
    if (initial.draft_id) return;   // server-side draft wins
    try {
      const raw = window.localStorage.getItem(autosaveKey);
      if (!raw) return;
      const snap = JSON.parse(raw);
      if (!snap || typeof snap !== 'object') return;
      if (snap.subject) setSubject(snap.subject);
      if (snap.body)    setBody(snap.body);
      if (snap.toStr)   setToStr(snap.toStr);
      if (snap.ccStr)   setCcStr(snap.ccStr);
      if (snap.bccStr)  setBccStr(snap.bccStr);
      if (snap.template_key) setTemplateKey(snap.template_key);
    } catch { /* corrupt draft — ignore */ }
  }, [open, autosaveKey, initial.draft_id]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => {
      try {
        window.localStorage.setItem(autosaveKey, JSON.stringify({
          subject, body, toStr, ccStr, bccStr,
          template_key: templateKey,
          _saved_at: Date.now(),
        }));
      } catch { /* quota — silently drop */ }
    }, 250);
    return () => clearTimeout(t);
  }, [open, autosaveKey, subject, body, toStr, ccStr, bccStr, templateKey]);

  const clearAutosave = useCallback(() => {
    try { window.localStorage.removeItem(autosaveKey); } catch { /* noop */ }
  }, [autosaveKey]);

  /* Load templates + drafts once on open */
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [t, d] = await Promise.all([
          api.get('/email/compose/templates'),
          api.get('/email/compose/drafts'),
        ]);
        setTemplates(t.data?.items || []);
        setDrafts(d.data?.items || []);
      } catch (e) {
        console.error('[ComposeAndApprove] init load failed', e);
      }
    })();
  }, [open]);

  /* v3.18-1303 — Root-cause fix for the "typing crashes the CRM"
   * defect.  Previously ``doPrepare`` was a useCallback whose deps
   * included subject/body/toStr/ccStr/bccStr/context, which meant it
   * was recreated on EVERY keystroke.  The dependent useEffect then
   * POSTed /email/compose/prepare per character, generating a network
   * storm and — worse — landing stale setState calls on an unmounted
   * tree when the operator closed the dialog.  Because no ancestor
   * ErrorBoundary caught the stale-render exception, the entire CRM
   * shell went white.
   *
   * The fix: read the mutable compose state through a ref so
   * ``doPrepare`` becomes stable and only re-fires on template
   * switch or dialog open.  Also gates all setState calls behind an
   * ``alive`` ref so nothing lands after unmount. */
  const composeStateRef = useRef({
    context, subject, body, toStr, ccStr, bccStr, inReplyTo, threadRootId,
  });
  useEffect(() => {
    composeStateRef.current = {
      context, subject, body, toStr, ccStr, bccStr, inReplyTo, threadRootId,
    };
  }, [context, subject, body, toStr, ccStr, bccStr, inReplyTo, threadRootId]);

  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  const doPrepare = useCallback(async (nextKey) => {
    setPreparing(true);
    try {
      const snap = composeStateRef.current;
      const { data } = await api.post('/email/compose/prepare', {
        template_key:   nextKey,
        context:        { ...snap.context, subject: snap.subject, body: snap.body },
        to:             chipsFromString(snap.toStr),
        cc:             chipsFromString(snap.ccStr),
        bcc:            chipsFromString(snap.bccStr),
        in_reply_to:    snap.inReplyTo,
        thread_root_id: snap.threadRootId,
      });
      if (!aliveRef.current) return;               // dropped — modal closed
      if (!data || typeof data !== 'object') return;
      setEnvelope(data);
      // Only overwrite subject/body when switching TO a non-blank template
      // (blank preserves whatever the operator has typed).
      if (nextKey !== 'blank') {
        setSubject(data.subject || '');
        setBody(data.body || '');
      } else {
        // For "blank", keep the operator's local subject/body but sync
        // the envelope's from/signature.
        if (!snap.subject && data.subject) setSubject(data.subject);
        if (!snap.body    && data.body)    setBody(data.body);
      }
    } catch (e) {
      if (!aliveRef.current) return;
      console.error('[ComposeAndApprove] prepare failed', e);
      toast.error(e?.response?.data?.detail || 'Failed to prepare compose');
    } finally {
      if (aliveRef.current) setPreparing(false);
    }
  }, []);   // stable — deps read through ref

  useEffect(() => {
    if (open) { doPrepare(templateKey); }
  }, [open, templateKey, doPrepare]);

  /* v3.18-1304 — Live Signature Preview.
   *
   * Calls GET /api/email/compose/signature/preview which invokes the
   * EXACT ``build_signature_html`` renderer used by the send pipeline.
   * We refresh:
   *   • on modal open
   *   • whenever the browser window regains focus (operator likely
   *     just tweaked their profile in another tab)
   *   • when the operator clicks the manual refresh button
   *   • when the ``/prepare`` envelope arrives (which carries a fresh
   *     signature_html from the same pipeline) — we prefer the
   *     dedicated endpoint but fall back to the envelope so the panel
   *     is never blank
   */
  const fetchSignaturePreview = useCallback(async () => {
    if (!open) return;
    setSigLoading(true);
    setSigError(null);
    try {
      const { data } = await api.get('/email/compose/signature/preview');
      if (!aliveRef.current) return;
      setSigPreview({
        signature_html:  data.signature_html  || '',
        signature_lines: data.signature_lines || [],
        signature_text:  data.signature_text  || '',
        profile_used:    data.profile_used    || null,
        version:         data.version         || '',
        renderer:        data.renderer        || '',
      });
    } catch (e) {
      if (!aliveRef.current) return;
      setSigError(e?.response?.data?.detail || 'Could not load signature preview.');
    } finally {
      if (aliveRef.current) setSigLoading(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !signatureAppended) return;
    fetchSignaturePreview();
    const onFocus = () => { fetchSignaturePreview(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [open, signatureAppended, fetchSignaturePreview]);

  /* Attachment handlers */
  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    e.target.value = ''; // allow re-selecting the same file
    let running = totalAttBytes;
    const next = [...attachments];
    for (const f of files) {
      if (f.size > MAX_ATTACHMENT_BYTES) {
        toast.error(`${f.name} exceeds 10 MB`);
        continue;
      }
      if (running + f.size > MAX_TOTAL_ATTACHMENT_BYTES) {
        toast.error('Total attachment size would exceed 25 MB');
        break;
      }
      const b64 = await fileToBase64(f);
      next.push({ filename: f.name, mime_type: f.type || 'application/octet-stream', data_base64: b64, _size: f.size });
      running += f.size;
    }
    setAttachments(next);
  };

  const removeAttachment = (idx) => setAttachments(attachments.filter((_, i) => i !== idx));

  /* Draft save / load / delete */
  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      const payload = {
        id:              draftId,
        template_key:    templateKey,
        subject,
        body,
        to:              chipsFromString(toStr),
        cc:              chipsFromString(ccStr),
        bcc:             chipsFromString(bccStr),
        attachments:     attachments.map(({ filename, mime_type, data_base64 }) => ({ filename, mime_type, data_base64 })),
        in_reply_to:     inReplyTo,
        thread_root_id:  threadRootId,
        client_id:       clientId,
      };
      const { data } = await api.post('/email/compose/drafts', payload);
      setDraftId(data.id);
      toast.success('Draft saved');
      const list = await api.get('/email/compose/drafts');
      setDrafts(list.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save draft');
    } finally {
      setSavingDraft(false);
    }
  };

  const loadDraft = async (id) => {
    try {
      const { data } = await api.get(`/email/compose/drafts/${id}`);
      const d = data.item || {};
      setDraftId(d.id);
      setTemplateKey(d.template_key || 'blank');
      setSubject(d.subject || '');
      setBody(d.body || '');
      setToStr((d.to || []).join(', '));
      setCcStr((d.cc || []).join(', '));
      setBccStr((d.bcc || []).join(', '));
      const atts = (d.attachments || []).map((a) => ({
        filename: a.filename,
        mime_type: a.mime_type,
        data_base64: a.data_base64,
        _size: Math.floor(((a.data_base64 || '').length * 3) / 4),
      }));
      setAttachments(atts);
      toast.success('Draft loaded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load draft');
    }
  };

  const deleteDraft = async (id) => {
    try {
      await api.delete(`/email/compose/drafts/${id}`);
      setDrafts(drafts.filter((d) => d.id !== id));
      if (draftId === id) setDraftId(null);
      toast.success('Draft deleted');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete draft');
    }
  };

  /* Send */
  const send = async () => {
    const toList = chipsFromString(toStr);
    if (!toList.length) { toast.error('Add at least one recipient'); return; }
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    setSending(true);
    try {
      const payload = {
        envelope_id:    envelope.envelope_id,
        subject,
        from_name:      envelope.from_name,
        from_address:   envelope.from_address,
        reply_to:       envelope.reply_to,
        to:             toList,
        cc:             chipsFromString(ccStr),
        bcc:            chipsFromString(bccStr),
        body,
        signature_text: sigPreview.signature_text || envelope.signature_text,
        attachments:    attachments.map(({ filename, mime_type, data_base64 }) => ({ filename, mime_type, data_base64 })),
        in_reply_to:    inReplyTo,
        thread_root_id: threadRootId,
        template_key:   templateKey,
        template_version: libraryTpl?.version || null,     // v3.18-1303
        template_source:  libraryTpl ? 'library' : (templateKey && templateKey !== 'blank' ? 'legacy' : null),
        client_id:      clientId,
        // v3.21-1621 · Recipient picked from the client selector.
        // Downstream callers (EmailComposeDialog / Compose gateway)
        // may use this to route audit / activity association.
        client_id_override: clientOverride?.id || null,
        recipient_client: clientOverride ? {
          id:            clientOverride.id,
          name:          clientOverride.name,
          email:         clientOverride.email,
          is_secondary:  !!clientOverride.is_secondary,
        } : null,
      };
      // v3.18-1290 — Specialized outbound endpoints (Final Push,
      // Paid In Full closure, funding email) opt out of the generic
      // /email/compose/send gateway by supplying onCustomSend, which
      // preserves their own audit trail + duplicate-detection while
      // giving operators the identical Compose & Approve UX.
      let data;
      if (typeof onCustomSend === 'function') {
        data = await onCustomSend(payload);
        if (data && data.__cancel) { setSending(false); return; }
        toast.success('Email sent');
      } else {
        const resp = await api.post('/email/compose/send', payload);
        data = resp.data;
        if (data.delivered) toast.success('Email sent'); else toast.warning('Send queued with delivery failures — see audit log');
      }
      // Auto-clean the draft that produced this send
      if (draftId) { try { await api.delete(`/email/compose/drafts/${draftId}`); } catch (_) { /* best-effort cleanup */ } }
      clearAutosave();   // v3.18-1303 — operator work is now delivered
      onSent?.(data);
      onOpenChange?.(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={!!open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl max-h-[92vh] overflow-hidden flex flex-col"
        data-testid="compose-and-approve-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {inReplyTo ? <Reply size={18} /> : (initial.forward ? <Forward size={18} /> : <Send size={18} />)}
            <span>Compose & Approve</span>
            {envelope.template_key && envelope.template_key !== 'blank' && (
              <Badge variant="secondary" data-testid="compose-current-template">{envelope.template_key}</Badge>
            )}
          </DialogTitle>
          <DialogDescription>
            Every operator email is signed with your live employee profile —
            edits are recorded to the audit log.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="compose" className="flex-1 overflow-hidden flex flex-col">
          <TabsList className="w-full justify-start" data-testid="compose-tabs">
            <TabsTrigger value="compose" data-testid="compose-tab-compose">Compose</TabsTrigger>
            <TabsTrigger value="drafts"  data-testid="compose-tab-drafts">Drafts <span className="ml-1 text-xs text-slate-500">({drafts.length})</span></TabsTrigger>
          </TabsList>

          <TabsContent value="compose" className="flex-1 overflow-auto space-y-3 mt-3">
            {/* Template + recipients */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label>Template</Label>
                <div className="flex gap-1">
                  <Select value={templateKey} onValueChange={setTemplateKey}>
                    <SelectTrigger data-testid="compose-template-select"><SelectValue placeholder="Choose a template" /></SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => (
                        <SelectItem key={t.key} value={t.key} data-testid={`compose-template-${t.key}`}>
                          <span className="text-xs text-slate-500 mr-1">[{t.category}]</span>{t.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setLibraryOpen(true)}
                    title="Browse the Corporate Template Library"
                    data-testid="compose-open-library"
                  >
                    <BookOpen size={14} />
                  </Button>
                </div>
                {libraryTpl && (
                  <div className="text-[10px] text-slate-500 mt-1 truncate">
                    Loaded: <span className="font-medium">{libraryTpl.name}</span>
                  </div>
                )}
              </div>
              <div className="col-span-2">
                <Label>From</Label>
                <Input
                  disabled
                  value={envelope.from_name ? `${envelope.from_name} <${envelope.from_address || ''}>` : (preparing ? 'Loading sender…' : '')}
                  data-testid="compose-from"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label>To</Label>
                {/* v3.21-1621 · Select-Client recipient picker.  Reps
                    can pick any Hub client (including Client 2 on a
                    household deal) OR keep typing a manual address. */}
                <ClientRecipientPicker
                  open={clientPickerOpen}
                  setOpen={setClientPickerOpen}
                  current={clientOverride}
                  onPick={(picked) => {
                    if (!picked) {
                      setClientOverride(null);
                      setToStr('');
                      setContext((c) => ({ ...c, client_first_name: 'there' }));
                    } else {
                      setClientOverride(picked);
                      setToStr(picked.email || '');
                      setContext((c) => ({
                        ...c,
                        client_first_name: (picked.first_name || (picked.name || '').split(' ')[0] || 'there'),
                      }));
                    }
                    setClientPickerOpen(false);
                  }}
                />
              </div>
              <Input value={toStr} onChange={(e) => setToStr(e.target.value)} placeholder="jane@example.com, joe@example.com" data-testid="compose-to" />
              {clientOverride && (
                <div className="mt-1 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1 flex items-center gap-2"
                     data-testid="compose-selected-client-chip">
                  <span className="font-semibold truncate">
                    {clientOverride.name}
                    {clientOverride.is_secondary && <span className="ml-1 text-[10px] font-bold text-emerald-700 bg-emerald-100 border border-emerald-300 px-1 rounded">Client 2</span>}
                  </span>
                  <span className="text-emerald-700 truncate">· {clientOverride.email}</span>
                  <button
                    type="button"
                    onClick={() => {
                      setClientOverride(null);
                      setToStr('');
                      setContext((c) => ({ ...c, client_first_name: 'there' }));
                    }}
                    className="ml-auto text-[11px] font-semibold text-emerald-700 hover:text-red-600"
                    data-testid="compose-clear-client"
                    title="Clear selected client (manual email still works)"
                  >Change / Remove</button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Cc</Label>
                <Input value={ccStr} onChange={(e) => setCcStr(e.target.value)} data-testid="compose-cc" />
              </div>
              <div>
                <Label>Bcc</Label>
                <Input value={bccStr} onChange={(e) => setBccStr(e.target.value)} data-testid="compose-bcc" />
              </div>
            </div>

            <div>
              <Label>Subject</Label>
              <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="compose-subject" />
            </div>

            {/* v3.18-1291 — client's local time chip visible during
                compose so operators see the recipient's clock BEFORE
                clicking Send. */}
            {clientId && (
              <ClientLocalTime clientId={clientId} variant="chip" />
            )}

            <div>
              <Label>Message</Label>
              <RichTextArea value={body} onChange={setBody} disabled={sending} testId="compose-body" />
            </div>

            {/* Attachments */}
            <div>
              <div className="flex items-center justify-between">
                <Label>Attachments</Label>
                <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="compose-add-attachment">
                  <Paperclip size={14} className="mr-1" /> Add file
                </Button>
                <input ref={fileInputRef} type="file" multiple hidden onChange={onPickFiles} data-testid="compose-file-input" />
              </div>
              {attachments.length > 0 && (
                <div className="mt-2 space-y-1">
                  {attachments.map((a, i) => (
                    <AttachmentRow key={`${a.filename}-${i}`} att={a} onRemove={() => removeAttachment(i)} />
                  ))}
                  <div className="text-xs text-slate-500 mt-1">
                    {attachments.length} file(s) — {Math.ceil(totalAttBytes / 1024)} KB
                  </div>
                </div>
              )}
            </div>

            {/* v3.18-1304 — Live Signature Preview (EMAIL-ARCH-002 Slice 3).
                Rendered by the EXACT ``build_signature_html`` pipeline the
                /send endpoint uses.  Collapsible, default-expanded, so
                operators always see the byte-for-byte block that will be
                delivered.  Refreshes on modal open, window focus, and via
                the manual refresh button so live employee profile edits
                land immediately. */}
            {!signatureAppended ? (
              <div
                className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                data-testid="compose-signature-not-appended"
              >
                <span className="uppercase tracking-wider text-[10px] font-medium mr-2">Signature</span>
                No signature will be added to this email. It is sent exactly as written above.
              </div>
            ) : (
            <div className="rounded border bg-white" data-testid="compose-signature-preview">
              <button
                type="button"
                onClick={() => setSigOpen((v) => !v)}
                className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
                data-testid="compose-signature-toggle"
                aria-expanded={sigOpen}
              >
                <span className="flex items-center gap-2">
                  <span className="uppercase tracking-wider text-[10px] text-slate-500">
                    Live Signature Preview
                  </span>
                  {sigPreview.profile_used?.full_name && (
                    <span className="text-slate-600">
                      · {sigPreview.profile_used.full_name}
                    </span>
                  )}
                  {sigLoading && <Loader2 size={12} className="animate-spin text-slate-400" />}
                </span>
                <span className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); fetchSignaturePreview(); }}
                    className="text-slate-500 hover:text-slate-900 p-1 rounded hover:bg-slate-100"
                    title="Refresh signature from your live profile"
                    data-testid="compose-signature-refresh"
                  >
                    <RefreshCw size={12} className={sigLoading ? 'animate-spin' : ''} />
                  </button>
                  {sigOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </span>
              </button>
              {sigOpen && (
                <div className="border-t px-3 py-3 bg-slate-50/50">
                  <div className="text-[10px] text-slate-500 mb-2">
                    Rendered by the send pipeline
                    {sigPreview.renderer && <> · <code className="font-mono">{sigPreview.renderer}</code></>}
                    {sigPreview.version && <> · {sigPreview.version}</>}
                    <span className="ml-2 italic">— this is exactly what will be embedded in the outgoing email.</span>
                  </div>
                  {sigError && (
                    <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-2"
                         data-testid="compose-signature-error">
                      {sigError}
                    </div>
                  )}
                  {sigPreview.signature_html ? (
                    <div
                      className="rounded border bg-white p-3"
                      data-testid="compose-signature-html"
                      /* SAFE: HTML source is the server-rendered signature
                         from services.email_signature (no user-controlled
                         input paths).  Same string that goes to SMTP2GO. */
                      dangerouslySetInnerHTML={{ __html: sigPreview.signature_html }}
                    />
                  ) : sigLoading ? (
                    <div className="text-slate-400 italic text-xs">Loading signature…</div>
                  ) : (
                    <div className="text-slate-400 italic text-xs">No signature available.</div>
                  )}

                  {/* Small structured profile echo so operators can spot
                      missing fields at a glance and go fix their profile. */}
                  {sigPreview.profile_used && (
                    <div className="mt-3 grid grid-cols-2 gap-x-3 gap-y-1 text-[11px] text-slate-600"
                         data-testid="compose-signature-profile">
                      {[
                        ['Name',       sigPreview.profile_used.full_name],
                        ['Title',      sigPreview.profile_used.title],
                        ['Department', sigPreview.profile_used.department],
                        ['Extension',  sigPreview.profile_used.extension],
                        ['Direct',     sigPreview.profile_used.direct_phone],
                        ['Mobile',     sigPreview.profile_used.mobile_phone],
                        ['Email',      sigPreview.profile_used.email],
                      ].map(([k, v]) => (
                        <div key={k} className="flex gap-1">
                          <span className="text-slate-400 w-20 shrink-0">{k}:</span>
                          <span className={v ? 'text-slate-800' : 'text-slate-400 italic'}>
                            {v || '(not configured)'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            )}
          </TabsContent>

          <TabsContent value="drafts" className="flex-1 overflow-auto mt-3">
            {drafts.length === 0 ? (
              <div className="text-center text-sm text-slate-500 py-8">No saved drafts.</div>
            ) : (
              <div className="space-y-2">
                {drafts.map((d) => (
                  <div key={d.id} className="flex items-center justify-between border rounded p-2 hover:bg-slate-50" data-testid={`compose-draft-${d.id}`}>
                    <div className="min-w-0 pr-2">
                      <div className="font-medium text-sm truncate">{d.subject || '(no subject)'}</div>
                      <div className="text-xs text-slate-500 truncate">to {(d.to || []).join(', ') || '—'} · {d.template_key || 'blank'} · {new Date(d.updated_at).toLocaleString()}</div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="secondary" onClick={() => loadDraft(d.id)} data-testid={`compose-draft-load-${d.id}`}>Load</Button>
                      <Button size="sm" variant="ghost"     onClick={() => deleteDraft(d.id)} data-testid={`compose-draft-del-${d.id}`}><Trash2 size={14} /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <Separator />
        <DialogFooter className="flex-row items-center justify-between gap-2">
          <div className="text-xs text-slate-500">
            {preparing ? <span className="flex items-center gap-1"><Loader2 className="animate-spin" size={12} /> Preparing envelope…</span> : `Envelope ${envelope.envelope_id?.slice(0,8) || '—'}`}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="ghost"   onClick={() => onOpenChange?.(false)} data-testid="compose-cancel">Cancel</Button>
            <Button type="button" variant="outline" onClick={saveDraft} disabled={savingDraft} data-testid="compose-save-draft">
              {savingDraft ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />}
              <span className="ml-1">Save Draft</span>
            </Button>
            <Button type="button" onClick={send} disabled={sending || preparing} data-testid="compose-send">
              {sending ? <Loader2 className="animate-spin" size={14} /> : <Send size={14} />}
              <span className="ml-1">Send</span>
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
      {/* v3.18-1302 EMAIL-ARCH-003 — enterprise template library picker */}
      <EmailTemplatePickerDialog
        open={libraryOpen}
        onOpenChange={setLibraryOpen}
        clientId={clientId}
        extraContext={context}
        onSelect={({ rendered, template }) => {
          setSubject(rendered.subject || '');
          setBody(rendered.body_text || '');
          setLibraryTpl(template);
          // Keep the underlying compose template key as 'blank' so the
          // /prepare re-render does not overwrite the library body.
          setTemplateKey('blank');
        }}
      />
    </Dialog>
  );
}
